// ============================================================================
// TARIFF ESTIMATION DISPLAY
// ============================================================================

class TariffDisplay {
  constructor() {
    this.standardDuty = 0.058;      // 5.8% normal import duty for HS 9006.91.00
    this.section122Low = 0.10;       // 10% Section 122 (current)
    this.section122High = 0.15;      // 15% Section 122 (announced increase)
    this.flatFee = 1800;             // $18 customs clearance fee in cents
    this.section122ExpiryDate = new Date('2026-07-24'); // Section 122 expires
    this.isUSMarket = false;
    
    this.init();
  }

  init() {
    this.checkMarket();
    
    if (!this.isUSMarket) return;
    
    this.waitForCartDrawer();
    document.addEventListener('cart:updated', () => this.updateTariff());
    this.observeDrawer();
  }

  checkMarket() {
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      this.isUSMarket = Shopify.country === 'US';
      return;
    }
    
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.isUSMarket = cart.currency === 'USD';
        if (this.isUSMarket) {
          this.waitForCartDrawer();
        }
      });
  }

  waitForCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      this.injectTariffDisplay();
    } else {
      setTimeout(() => this.waitForCartDrawer(), 100);
    }
  }

  observeDrawer() {
    const observer = new MutationObserver(() => {
      if (!document.querySelector('.tariff-estimate')) {
        this.injectTariffDisplay();
      }
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  injectTariffDisplay() {
    const footerBottom = document.querySelector('.drawer__footer-bottom .grid.gap-4');
    
    if (!footerBottom || document.querySelector('.tariff-estimate')) {
      return;
    }

    const now = new Date();
    const isSection122Active = now < this.section122ExpiryDate;
    
    const estimateText = isSection122Active 
      ? 'Estimated US import duties (15.8-20.8% + $18 fee). Not included in subtotal.'
      : 'Estimated US import duties (5.8% + $18 fee). Not included in subtotal.';

    const tariffHTML = `
      <div class="tariff-estimate grid gap-3 p-4 rounded" style="
        border: 1px solid rgba(var(--color-foreground), 0.15);
        background: rgba(var(--color-foreground), 0.03);
      ">
        <div class="flex items-center justify-between">
          <span class="flex items-center gap-2">
            <svg class="icon icon--md" width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 4v4.5M8 11h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span class="font-medium text-base">Estimated US Import Duties</span>
          </span>
          <span class="tariff-amount font-bold text-lg" data-tariff-amount>$0.00</span>
        </div>
        <p class="text-xs text-opacity leading-tight" style="margin-top: -4px;">
          ${estimateText}
        </p>
      </div>
    `;

    footerBottom.insertAdjacentHTML('afterbegin', tariffHTML);
    this.updateTariff();
  }

  async updateTariff() {
    if (!this.isUSMarket) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart.item_count === 0) return;
      
      const tariffAmount = this.calculateTariff(cart);
      
      const amountElement = document.querySelector('[data-tariff-amount]');
      if (amountElement) {
        amountElement.textContent = this.formatMoney(tariffAmount);
      }
    } catch (error) {
      console.error('Error updating tariff:', error);
    }
  }

  calculateTariff(cart) {
    const now = new Date();
    const isSection122Active = now < this.section122ExpiryDate;
    
    // Calculate duties on cart total
    const standardDutyAmount = Math.round(cart.total_price * this.standardDuty);
    
    let section122Amount = 0;
    if (isSection122Active) {
      // Use the high estimate (15%) for customer awareness
      section122Amount = Math.round(cart.total_price * this.section122High);
    }
    
    const totalDuties = standardDutyAmount + section122Amount + this.flatFee;
    
    console.log(`[Tariff] Cart total: $${cart.total_price/100}`);
    console.log(`[Tariff] Standard duty (5.8%): $${standardDutyAmount/100}`);
    if (isSection122Active) {
      console.log(`[Tariff] Section 122 (15%): $${section122Amount/100}`);
    }
    console.log(`[Tariff] Clearance fee: $18`);
    console.log(`[Tariff] Total estimate: $${totalDuties/100}`);
    
    return totalDuties;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }
}


// ============================================================================
// TERMS AGREEMENT MODAL
// Re-enable by uncommenting the TermsModal class below and updating the
// initialization block at the bottom to include: new TermsModal();
// ============================================================================

/*

class TermsModal {
  constructor() {
    this.isUSMarket = false;
    this.pendingAction = null;
    this.modalOpenTime = null;
    this.hasAgreedBefore = false;
    
    console.log('[Terms Modal] Initializing...');
    this.init();
  }

  init() {
    this.checkMarket();
    
    if (!this.isUSMarket) {
      console.log('[Terms Modal] Not US market');
      return;
    }
    
    this.checkPreviousAgreement();
    
    console.log('[Terms Modal] US market detected');
    this.injectTermsModal();
    this.interceptCheckoutButtons();
  }

  checkPreviousAgreement() {
    try {
      const agreementData = localStorage.getItem('us_terms_agreement');
      if (agreementData) {
        const data = JSON.parse(agreementData);
        const expiryDate = new Date(data.expiry);
        const now = new Date();
        
        if (now < expiryDate) {
          this.hasAgreedBefore = true;
          console.log('[Terms Modal] User has agreed before, expires:', expiryDate);
        } else {
          localStorage.removeItem('us_terms_agreement');
          console.log('[Terms Modal] Previous agreement expired');
        }
      }
    } catch (error) {
      console.error('[Terms Modal] Error checking previous agreement:', error);
    }
  }

  saveAgreement() {
    try {
      const now = new Date();
      const expiry = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days
      
      const agreementData = {
        agreed: true,
        timestamp: now.toISOString(),
        expiry: expiry.toISOString()
      };
      
      localStorage.setItem('us_terms_agreement', JSON.stringify(agreementData));
      console.log('[Terms Modal] Agreement saved, expires:', expiry);
    } catch (error) {
      console.error('[Terms Modal] Error saving agreement:', error);
    }
  }

  checkMarket() {
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      this.isUSMarket = Shopify.country === 'US';
      console.log('[Terms Modal] Market check (Shopify.country):', Shopify.country);
      return;
    }
    
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.isUSMarket = cart.currency === 'USD';
        console.log('[Terms Modal] Market check (currency):', cart.currency);
        if (this.isUSMarket) {
          this.checkPreviousAgreement();
          this.injectTermsModal();
          this.interceptCheckoutButtons();
        }
      });
  }

  injectTermsModal() {
    if (document.getElementById('us-terms-modal')) {
      console.log('[Terms Modal] Modal already exists');
      return;
    }

    console.log('[Terms Modal] Injecting modal...');

    const modalHTML = `
      <style>
        @media (max-width: 640px) {
          .us-terms-modal .us-terms-content {
            padding: 1.5rem !important;
            max-height: 90vh !important;
          }
          .us-terms-modal h2 {
            font-size: 1.25rem !important;
            margin-bottom: 1rem !important;
          }
          .us-terms-modal .terms-body {
            font-size: 0.875rem !important;
          }
          .us-terms-modal .terms-buttons {
            flex-direction: column !important;
          }
          .us-terms-modal .terms-buttons button {
            width: 100% !important;
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        
        .shake-animation {
          animation: shake 0.5s;
        }
      </style>
      <div id="us-terms-modal" class="us-terms-modal" style="
        display: none;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        z-index: 999999;
        align-items: center;
        justify-content: center;
        padding: 1rem;
      ">
        <div class="us-terms-content" style="
          background: white;
          padding: 2.5rem;
          border-radius: 34px;
          max-width: 600px;
          width: 100%;
          max-height: 85vh;
          overflow-y: auto;
          position: relative;
          box-sizing: border-box;
        ">
          <h2 style="margin: 0 0 1.25rem 0; font-size: 1.5rem; font-weight: bold; line-height: 1.3;">
            US Import Duties & Shipping
          </h2>
          
          <div class="terms-body" style="margin-bottom: 1.5rem; line-height: 1.6; color: #333; font-size: 0.95rem;">
            <p style="margin-bottom: 1rem;">
              Orders shipped to the United States are subject to import duties and customs fees. These charges are not included at checkout and are payable by you upon delivery.
            </p>
            
            <p style="margin-bottom: 1rem;">
              <strong>Current import duties:</strong> Approximately 15.8-20.8% of order value plus an $18 customs clearance fee. This includes the standard 5.8% import duty plus a temporary 10-15% surcharge (valid through July 24, 2026).
            </p>
            
            <p style="margin-bottom: 1rem;">
              If a shipment is refused or unclaimed, original shipping costs are non-refundable and return shipping will be deducted from any refund. Full details are in our <a href="https://filmatura.com/policies/refund-policy" target="_blank" style="color: #0066cc; text-decoration: underline;">Refund Policy</a>.
            </p>
            
            <p style="margin-top: 1.25rem; padding: 1rem; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px; font-size: 0.9rem;">
              <strong>By completing your purchase, you acknowledge responsibility for all applicable import duties, customs fees, and shipping costs.</strong>
            </p>
          </div>

          <div id="terms-error-message" style="
            display: none;
            margin-bottom: 1rem;
            padding: 0.75rem 1rem;
            background: #fee;
            border: 1px solid #fcc;
            border-radius: 8px;
            color: #c33;
            font-size: 0.9rem;
            text-align: center;
          ">
            Please take a moment to read the terms above before proceeding.
          </div>

          <div class="terms-buttons" style="display: flex; gap: 0.75rem; margin-top: 2rem;">
            <button id="us-terms-decline" style="
              flex: 1;
              padding: 1.125rem 2rem;
              border: 2px solid #dc3545;
              background: white;
              color: #dc3545;
              font-size: 1rem;
              font-weight: 600;
              border-radius: 100px;
              cursor: pointer;
              white-space: nowrap;
            ">
              Cancel
            </button>
            <button id="us-terms-agree" style="
              flex: 1;
              padding: 1.125rem 2rem;
              border: none;
              background: #000;
              color: #fff;
              font-size: 1rem;
              font-weight: 600;
              border-radius: 100px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 0.5rem;
              white-space: nowrap;
            ">
              <span>I Agree & Proceed</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
                <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    console.log('[Terms Modal] Modal injected');

    document.getElementById('us-terms-agree').addEventListener('click', () => this.agreeToTerms());
    document.getElementById('us-terms-decline').addEventListener('click', () => this.closeModal());
  }

  isInsideCartDrawer(element) {
    const isInside = element.closest('cart-drawer') !== null;
    return isInside;
  }

  interceptCheckoutButtons() {
    console.log('[Terms Modal] Setting up button interception...');
    
    document.addEventListener('click', (e) => {
      if (!this.isUSMarket) return;

      const checkoutButton = e.target.closest('button[name="checkout"]');
      const viewCartLink = e.target.closest('a[href="/cart"]');
      
      let shouldIntercept = false;
      let actionType = null;
      let targetElement = null;
      
      if (checkoutButton && this.isInsideCartDrawer(checkoutButton)) {
        shouldIntercept = true;
        actionType = 'checkout';
        targetElement = checkoutButton;
        console.log('[Terms Modal] Will intercept CHECKOUT');
      } else if (viewCartLink && this.isInsideCartDrawer(viewCartLink)) {
        shouldIntercept = true;
        actionType = 'cart';
        targetElement = viewCartLink;
        console.log('[Terms Modal] Will intercept VIEW CART');
      }
      
      if (shouldIntercept) {
        console.log('[Terms Modal] INTERCEPTING! Showing modal...');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.pendingAction = {
          type: actionType,
          element: targetElement
        };
        
        console.log('[Terms Modal] Pending action stored:', this.pendingAction);
        this.showModal();
        return false;
      }
    }, true);
    
    console.log('[Terms Modal] Button interception setup complete');
  }

  showModal() {
    console.log('[Terms Modal] Showing modal...');
    const modal = document.getElementById('us-terms-modal');
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      
      if (!this.hasAgreedBefore) {
        this.modalOpenTime = Date.now();
        console.log('[Terms Modal] Modal opened at:', this.modalOpenTime);
      } else {
        console.log('[Terms Modal] User has agreed before, no timer needed');
      }
      
      console.log('[Terms Modal] Modal displayed');
    } else {
      console.error('[Terms Modal] Modal element not found!');
    }
  }

  closeModal(clearPendingAction = true) {
    console.log('[Terms Modal] Closing modal...');
    const modal = document.getElementById('us-terms-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
    if (clearPendingAction) {
      this.pendingAction = null;
    }
    
    const errorMsg = document.getElementById('terms-error-message');
    if (errorMsg) {
      errorMsg.style.display = 'none';
    }
  }

  agreeToTerms() {
    console.log('[Terms Modal] User agreed to terms');
    console.log('[Terms Modal] Has agreed before:', this.hasAgreedBefore);
    
    if (!this.hasAgreedBefore && this.modalOpenTime) {
      const timeElapsed = Date.now() - this.modalOpenTime;
      console.log('[Terms Modal] Time elapsed:', timeElapsed, 'ms');
      
      if (timeElapsed < 3000) {
        console.log('[Terms Modal] Too fast! User must wait.');
        
        const errorMsg = document.getElementById('terms-error-message');
        const modalContent = document.querySelector('.us-terms-content');
        
        if (errorMsg && modalContent) {
          errorMsg.style.display = 'block';
          modalContent.classList.add('shake-animation');
          setTimeout(() => {
            modalContent.classList.remove('shake-animation');
          }, 500);
          modalContent.scrollTop = 0;
        }
        
        return;
      }
    }
    
    console.log('[Terms Modal] Proceeding with agreement');
    console.log('[Terms Modal] Pending action:', this.pendingAction);
    
    this.saveAgreement();
    this.hasAgreedBefore = true;
    
    const savedAction = this.pendingAction;
    this.closeModal(false);

    setTimeout(() => {
      console.log('[Terms Modal] Executing saved action:', savedAction);
      
      if (savedAction) {
        const element = savedAction.element;
        console.log('[Terms Modal] Target element:', element);
        
        if (savedAction.type === 'checkout') {
          console.log('[Terms Modal] Triggering checkout...');
          
          const form = element.closest('form');
          console.log('[Terms Modal] Form found:', form);
          
          if (form) {
            const checkoutInput = document.createElement('input');
            checkoutInput.type = 'hidden';
            checkoutInput.name = 'checkout';
            checkoutInput.value = '';
            form.appendChild(checkoutInput);
            
            console.log('[Terms Modal] Added checkout input, submitting form...');
            form.submit();
          } else {
            console.log('[Terms Modal] No form found, navigating to /checkout');
            window.location.href = '/checkout';
          }
        } else if (savedAction.type === 'cart') {
          console.log('[Terms Modal] Navigating to cart...');
          window.location.href = '/cart';
        }
        
        this.pendingAction = null;
      } else {
        console.error('[Terms Modal] No saved action found!');
      }
    }, 100);
  }
}

*/


// ============================================================================
// INITIALIZATION
// To re-enable the terms modal: uncomment the TermsModal class above
// and add `new TermsModal();` to both blocks below.
// ============================================================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TariffDisplay();
    // new TermsModal();
  });
} else {
  new TariffDisplay();
  // new TermsModal();
}